import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, VolumeX } from "lucide-react";

const AUDIO_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_gallery-story/artifacts/wyvfcmvn_194868__juskiddink__waves-on-a-sandy-beach.mp3";

const STORAGE_KEY = "em_ambient_muted";
const DEFAULT_VOLUME = 0.75;

/**
 * Site-wide ambient audio player.
 * - Autoplay muted on mount (browsers require gesture for unmuted autoplay).
 * - Unmutes on the first user gesture (click / key) unless the user has previously chosen mute.
 * - A subtle floating speaker toggle sits bottom-right; state persists in localStorage.
 */
export const AmbientAudio = () => {
  const audioRef = useRef(null);
  const location = useLocation();
  // The DJ page is a music-first experience — never play ambient sound there
  // so it doesn't fight with Spotify or the guest's request feedback.
  const forceMute = /^\/[a-z]{2}\/dj(\/|$)/i.test(location.pathname);
  const [muted, setMuted] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      // Default: NOT muted (user asked for "by default"). But if user has explicitly muted before, respect it.
      if (raw === null) return false;
      return raw === "true";
    } catch {
      return false;
    }
  });
  const [ready, setReady] = useState(false);

  const persistMuted = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  }, []);

  // Set up the audio element once
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = DEFAULT_VOLUME;
    el.loop = true;
    el.preload = "auto";
    // Always start muted for autoplay compatibility; unmute on first gesture (if user hasn't opted out).
    el.muted = true;

    const onCanPlay = () => setReady(true);
    el.addEventListener("canplaythrough", onCanPlay, { once: true });

    // Try autoplay muted (allowed by browsers)
    el.play().catch(() => {
      /* Ignore; will start on next user gesture. */
    });

    return () => {
      el.removeEventListener("canplaythrough", onCanPlay);
    };
  }, []);

  // On first user gesture: if user hasn't chosen mute AND we're not force-muted (e.g. on /dj),
  // unmute and (re)play. Skip entirely on the DJ page so a click there can never unmute.
  useEffect(() => {
    if (muted || forceMute) return undefined; // user explicitly muted, or DJ page → do nothing
    const el = audioRef.current;
    if (!el) return undefined;

    const kick = () => {
      try {
        el.muted = false;
        el.volume = DEFAULT_VOLUME;
        const p = el.play();
        if (p && typeof p.then === "function") p.catch(() => {});
      } catch {
        /* ignore */
      }
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
    };
    window.addEventListener("pointerdown", kick, { once: true });
    window.addEventListener("keydown", kick, { once: true });
    window.addEventListener("touchstart", kick, { once: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
    };
  }, [muted, forceMute]);

  // Sync muted → element (respecting forceMute on /dj)
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const effectiveMuted = muted || forceMute;
    el.muted = effectiveMuted;
    if (!effectiveMuted) {
      const p = el.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    } else {
      // Actually pause on /dj so the audio pipeline is quiet.
      try { el.pause(); } catch { /* ignore */ }
    }
  }, [muted, forceMute]);

  const toggle = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      persistMuted(next);
      return next;
    });
  }, [persistMuted]);

  return (
    <>
      <audio
        ref={audioRef}
        src={AUDIO_URL}
        preload="auto"
        loop
        playsInline
        aria-hidden="true"
        data-testid="ambient-audio-element"
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={muted ? "Turn ambient sound on" : "Turn ambient sound off"}
        aria-pressed={!muted && !forceMute}
        data-testid="ambient-audio-toggle"
        title={forceMute ? "Muted on the DJ page" : (muted ? "Sound off — click to enable" : "Sound on — click to mute")}
        className="fixed z-40 bottom-5 left-5 sm:bottom-6 sm:left-6 h-11 w-11 rounded-full flex items-center justify-center bg-background/85 backdrop-blur border border-border shadow-[0_8px_24px_rgba(31,31,31,0.10)] text-foreground/75 hover:text-foreground hover:bg-background transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {muted || forceMute ? (
          <VolumeX className="w-4 h-4" aria-hidden="true" />
        ) : (
          <Volume2 className={`w-4 h-4 ${ready ? "" : "opacity-70"}`} aria-hidden="true" />
        )}
      </button>
    </>
  );
};

export default AmbientAudio;
